import { Mail, MapPin, Phone, MessageCircle } from "lucide-react";
import { useCompany, buildWhatsAppUrl } from "@/lib/useCompany";

const MAP_QUERY = encodeURIComponent("Cerro San Luis 9880, Quilicura, Santiago, Chile");
const MAP_EMBED_URL = `https://www.google.com/maps?q=${MAP_QUERY}&output=embed`;
const MAP_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${MAP_QUERY}`;

export default function Footer() {
  const company = useCompany();
  const year = new Date().getFullYear();

  return (
    <footer
      id="contacto"
      data-testid="site-footer"
      className="bg-vp-navy-deep text-white border-t-2 border-vp-yellow"
    >
      {/* Map block */}
      <div className="border-b-2 border-vp-yellow">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-5 px-6 lg:px-12 py-12 lg:py-16 border-b-2 lg:border-b-0 lg:border-r-2 border-white/15 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-[2px] bg-vp-yellow" />
                <span className="label-industrial">06 · Encuéntranos</span>
              </div>
              <h2 className="heading-industrial text-3xl lg:text-5xl text-white mb-4">
                Patio Vigopal
                <br />
                <span className="text-vp-yellow">Quilicura.</span>
              </h2>
              <p className="text-white/75 mb-8 max-w-md">
                Visítanos para evaluar tu pedido o coordinar retiro. Recepción
                de camiones con cita previa.
              </p>

              <ul className="space-y-4 text-white/85">
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="mt-1 text-vp-yellow flex-shrink-0" />
                  <span>{company.address || "Cerro San Luis 9880, Quilicura"}</span>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={18} className="mt-1 text-vp-yellow flex-shrink-0" />
                  <a href={`tel:${company.phone}`} className="hover:text-vp-yellow">
                    {company.phone || "—"}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={18} className="mt-1 text-vp-yellow flex-shrink-0" />
                  <a
                    href={`mailto:${company.email}`}
                    className="hover:text-vp-yellow break-all"
                  >
                    {company.email || "—"}
                  </a>
                </li>
              </ul>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={buildWhatsAppUrl(company.whatsapp)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp-btn"
                  className="inline-flex items-center gap-2 bg-vp-red hover:bg-vp-yellow hover:text-[#001E62] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 transition-colors"
                >
                  <MessageCircle size={14} />
                  WhatsApp
                </a>
                <a
                  href={MAP_DIRECTIONS_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-directions-btn"
                  className="inline-flex items-center gap-2 border-2 border-vp-yellow hover:bg-vp-yellow hover:text-[#001E62] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 transition-colors"
                >
                  <MapPin size={14} />
                  Cómo llegar
                </a>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-white/15">
              <div className="label-industrial mb-2">Atención corporativa</div>
              <div className="text-sm text-white/80">
                <div><span className="font-bold text-white">Lun – Vie</span> · 08:30 – 18:30</div>
                <div><span className="font-bold text-white">Sábado</span> · 09:00 – 13:00</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 relative min-h-[420px] lg:min-h-full bg-vp-navy">
            <iframe
              title="Ubicación Vigopal — Cerro San Luis 9880, Quilicura"
              data-testid="footer-map"
              src={MAP_EMBED_URL}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full grayscale-[20%] contrast-[0.9]"
              style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
              allowFullScreen
            />
            {/* corner badge */}
            <div className="absolute top-4 left-4 bg-vp-yellow text-[#001E62] px-4 py-2 font-bold uppercase tracking-widest text-[11px] z-10">
              Patio Operativo · Quilicura
            </div>
          </div>
        </div>
      </div>

      {/* Big logo / closing */}
      <div className="border-b-2 border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 flex items-center gap-6">
            <img
              src="/logo-vigopal.png"
              alt="Vigopal logo"
              className="w-20 h-20 object-contain border-2 border-vp-yellow"
            />
            <div>
              <div className="heading-industrial text-5xl lg:text-7xl text-white leading-none">
                VIGOPAL
              </div>
              <div className="label-industrial mt-2">Industria · Logística · Pallets</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/80 text-lg leading-relaxed">
              5 años moviendo la carga de la industria local de manera
              eficiente. <span className="text-vp-yellow font-bold">Compra y venta de pallets</span> para
              operaciones que no pueden detenerse.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-white/50 text-xs">
        <div>© {year} Vigopal · Todos los derechos reservados</div>
        <a
          href="/admin"
          data-testid="footer-admin-link"
          className="font-bold uppercase tracking-widest hover:text-vp-yellow transition-colors"
        >
          Acceso interno →
        </a>
      </div>
    </footer>
  );
}
